import { type RootState } from "~/app/store";
import { createSelector } from "reselect";
import { type LineItem, isProductGroup, type EntryGroup } from "~/features/cart/cart/models/CartData";
import {
  selectIsCartAboveThreshold,
  selectLineItemsByGroupNumber,
} from "~/features/cart/cart/redux/selectors/cartSelectors";
import { selectBundleGroups, selectOrderableGroup } from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import uniq from "lodash/uniq";
import { isKITProduct } from "~/domains/product/types/ProductData";
import type { CartDescriptorFor } from "~/features/cart/cart/pna/cartPna";

export interface InvalidCartItem {
  tableId: string;
  productGroupNumber: number;
}

const selectPackEntries = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (packageEntriesById) => Object.values(packageEntriesById) as LineItem[],
);
const isInvalid = (item: LineItem) => !item.valid;
const isMissingPackSize = (item: LineItem) => item.packSize === undefined;
export const selectDisableCheckout = (externalDisableCheckout: boolean) => (state: RootState) => {
  if (externalDisableCheckout) {
    return true;
  }

  const packEntries = selectPackEntries(state);

  if (!selectIsCartAboveThreshold(state)) {
    if (packEntries.some(isMissingPackSize)) {
      return true;
    }
  }

  if (packEntries.some(isInvalid)) {
    return true;
  }
};
export const selectIsRequestInFlight = (state: RootState) => state.cartPage.requestInFlight;
export const selectAccountNumber = (state: RootState) => state.cartPage.accountNumber;
export const selectIsDstError = (state: RootState) => state.cartPage.dstError;

// A07: selectIsPnaLoading was deleted — the PnA loading aggregate derives from
// `useCartPnaQueries().isPnaLoading` (query pending states) in the cart view model.

export const selectCartContainsHousingKIT = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (state: RootState) => state.products.basic,
  (packageEntriesById, basic) => {
    const productCodes = uniq((Object.values(packageEntriesById) as LineItem[]).map((e) => e.productCode));
    return productCodes.some((code) => isKITProduct(basic[code]));
  },
);

/**
 * Inputs for {@link collectInvalidItems}: the group trees and their line items (A07 re-home of
 * the former `selectInvalidItems` selector — descriptors now come from a descriptor source
 * instead of the legacy `state.products` dictionaries).
 */
export const selectInvalidItemsInputs = createSelector(
  selectOrderableGroup,
  selectBundleGroups,
  selectLineItemsByGroupNumber,
  (orderableGroup, bundleGroups, lineItemsByGroupNumber) => ({
    orderableGroup,
    bundleGroups,
    lineItemsByGroupNumber,
  }),
);

/** Pure invalid-item walk over the group trees using query-backed descriptors (A07). */
export function collectInvalidItems(
  inputs: ReturnType<typeof selectInvalidItemsInputs>,
  descriptorFor: CartDescriptorFor,
): InvalidCartItem[] {
  const { orderableGroup, bundleGroups, lineItemsByGroupNumber } = inputs;
  const invalidEntries: InvalidCartItem[] = [];

  const processGroup = (topGroup: NonNullable<typeof orderableGroup> | (typeof bundleGroups)[number]) => {
    const tableId = `table:${topGroup.groupType}:${topGroup.groupNumber}:${
      "quoteNumber" in topGroup ? topGroup.quoteNumber : ""
    }`;
    function walk(node: EntryGroup): void {
      if (isProductGroup(node)) {
        const groupLineItems = lineItemsByGroupNumber[node.groupNumber] ?? [];
        const hasInvalid = groupLineItems.some((e) => {
          const descriptor = descriptorFor(e.productCode, e.priceReference);
          return !e.valid || descriptor.isError;
        });
        if (hasInvalid) {
          invalidEntries.push({ tableId, productGroupNumber: node.groupNumber });
        }
      }
      node.children.forEach(walk);
    }
    topGroup.children.forEach(walk);
  };

  if (orderableGroup) {
    processGroup(orderableGroup);
  }
  bundleGroups.forEach(processGroup);

  return invalidEntries;
}
